<template>
  <div class="full-height">
    <spinner v-if="loading" />

    <div v-else>
      <template v-if="isCreate || isEdit">
        <alert class="slds-m-bottom--xx-small"  :message="$t('fastCounter.targetDefinition.rulesAlert')" type="message" />

        <!-- Bookmarks -->
        <card additional-classes="slds-card_boundary slds-m-bottom">
          <accordion>
            <div class="slds-form-element" v-if="bookmarks.length">
              <SLDSInput
                :label="$t('fastCounter.targetDefinition.filterBookmarks')"
                :placeholder="$t('action.filter')"
                class="slds-m-bottom--small"
                has-left-icon
                type="text"
                v-model="localSearchKey"
                @keyup-enter="filterBookmarks">
                  <use xlink:href="../../assets/symbols.svg#search" slot="leftIcon"></use>
              </SLDSInput>
            </div>
            <p v-if="!filteredBookmarks.length">No results</p>
            <accordion-item :title="$t('queryBuilder.bookmarks')">
              <accordion>
                <card additional-classes="slds-card_boundary" v-if="bookmarks.length">
                  <draggable @start="dragStart" @end="dragEnd" v-model="filteredBookmarks" group="rules">
                    <accordion-item v-for="(bookmark, index) in rowsToShow" :key="index" :title="bookmark.segmentName" :accordion-item-id="`${index}`">
                      <template #actions>
                        <div class="slds-text-body_small">
                          {{$t('fastCounter.targetDefinition.bookmarkCreatedBy', {user : bookmark.createdByName, date: $options.filters.formatDate(bookmark.createdDate, $t('general.dateFormat'))})}}
                        </div>

                        <menu-button
                          hide-check
                          labels-prefix="queryBuilder"
                          position="right"
                          :options="getBookmarkActions(bookmark)"
                          @select="handleSelect($event, bookmark)"/>
                      </template>

                      <group scope="fastCounter" :value="bookmark" :config="config" :brands="brands" />

                    </accordion-item>
                  </draggable>
                  <div v-if="showPaginationSection" class="pagination-section slds-m-top--medium">
                    <div>
                      <button class="slds-button slds-button_neutral" @click="goToFirstPage" :disabled="pageNumber === 0"> &lt;&lt; </button>
                      <button class="slds-button slds-button_neutral slds-m-right--small" @click="goToPreviousPage" :disabled="pageNumber === 0"> &lt; </button>
                      <span> {{ pageNumber + 1 }} / {{ pageNumbers + 1 }} </span>
                      <button class="slds-button slds-button_neutral slds-m-left--small" @click="goToNextPage" :disabled="isNextButtonDisabled"> &gt; </button>
                      <button class="slds-button slds-button_neutral" @click="goToLastPage" :disabled="isNextButtonDisabled"> &gt;&gt; </button>
                    </div>
                  </div>
                </card>
                <span v-else>{{$t('queryBuilder.noBookmark')}}</span>
              </accordion>
            </accordion-item>
          </accordion>
        </card>
      </template>

      <card class="slds-card_boundary full-height" :title="$t('campaignDesigner.targetDefinition.segments')" has-icon>
        <template #icon>
          <span class="slds-icon_container slds-icon-standard-segments">
            <svg class="slds-icon slds-icon-standard-segments" aria-hidden="true">
              <use xlink:href="../../assets/symbols.svg#segments"></use>
            </svg>
          </span>
        </template>
        <template #actions v-if="isCreate || isEdit">
          <ul class="slds-button-group-list">
            <li>
              <button class="slds-button slds-button_neutral" :disabled="!canAddRule" @click="addRule">
                <svg class="slds-button__icon slds-button__icon_left" aria-hidden="true">
                  <use xlink:href="../../assets/symbols.svg#add"></use>
                </svg>
                {{$t('campaignDesigner.targetDefinition.addRule')}}
              </button>
            </li>
            <li>
              <button class="slds-button slds-button_neutral" :disabled="!canAddRule" @click="showModal">
                <svg class="slds-button__icon slds-button__icon_left" aria-hidden="true">
                  <use xlink:href="../../assets/symbols.svg#add"></use>
                </svg>
                {{$t('campaignDesigner.targetDefinition.addRuleGroup')}}
              </button>
            </li>
            <li>
              <button class="slds-button slds-button_neutral" :disabled="!localValue.conditions" @click="openPreviewModal">
                <svg class="slds-button__icon slds-button__icon_left" aria-hidden="true">
                  <use xlink:href="../../assets/symbols.svg#preview"></use>
                </svg>
                {{$t('campaignDesigner.targetDefinition.preview')}}
              </button>
            </li>
            <li>
              <button class="slds-button slds-button_neutral" :disabled="!localValue.conditions" @click="openBookmarkModal">
                <svg class="slds-button__icon slds-button__icon_left" aria-hidden="true">
                  <use xlink:href="../../assets/symbols.svg#save"></use>
                </svg>
                {{$t('campaignDesigner.targetDefinition.saveSectionAs')}}
              </button>
            </li>
          </ul>
        </template>

        <draggable v-model="localValue.conditions" group="rules">
          <rule-groups ref="groups" scope="fastCounter" v-model="localValue" :brands="brands" :editable="isCreate || isEdit" @deleteRule="deleteRule" :config="config" :standard-rules="standardRules" />
        </draggable>
      </card>
    </div>

    <transition name="fade">
      <new-group-modal
        @newFromBookmark="createFromBookmark"
        @newGroup="createGroup"
        @close="closeModal"
        :config="config"
        modal-action="add"
        v-if="showAddGroupModal" />
    </transition>

    <transition name="fade">
      <save-bookmark-modal-from-target-definition
        @close="showSaveBookmarkModal = false"
        :bookmark="localValue"
        scope="fastCounter"
        v-if="showSaveBookmarkModal" />
    </transition>

    <transition name="fade">
      <modal
        :title="$t('queryBuilder.deleteBookmark.title')"
        :destructive-button-text="$t('action.delete')"
        @close="bookmarkToDeleteId = ''"
        @destructiveButtonClick="doDeleteBookmark"
        v-if="bookmarkToDeleteId">
        {{$t('queryBuilder.deleteBookmark.message')}}
      </modal>
    </transition>

    <transition name="fade">
      <modal
        :title="$t('campaignDesigner.targetDefinition.preview')"
        @close="showPreviewModal = false"
        size="large"
        v-if="showPreviewModal">
          <rule-groups
            scope="fastCounter"
            :brands="brands"
            :config="config"
            :standard-rules="standardRules"
            :value="localValue" />
      </modal>
    </transition>
  </div>
</template>
<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { validate } from '../utils/utils'
import Accordion from '../slds/Accordion'
import AccordionItem from '../slds/AccordionItem'
import Alert from '../slds/Alert'
import Card from '../slds/Card'
import Draggable from 'vuedraggable'
import EventBus from '../utils/EventBus'
import Group from '../campaign-manager/sales/target-definition/Group'
import HasMode from '../utils/HasMode'
import MenuButton from '../slds/MenuButton'
import Modal from '../Modal'
import RuleGroups from '../campaign-manager/sales/target-definition/RuleGroups'
import SaveBookmarkModal from '../campaign-manager/sales/target-definition/SaveBookmarkModal'
import Spinner from '../Spinner'
import CheckPermission from '../../components/utils/CheckPermission'
import SLDSInput from '../../components/slds/Input'
import { applyFiltersToList } from '../../components/utils/utils'

const TARGET_SOURCE = 'fast_counter'
const PAGE_SIZE = 10

import {  getDb, insert, readFirstLine } from '..//utils/indexedDbUtils'
export default {
  watch: {
    'bookmarks.length'() {
      this.getBookmarks()
    }
  },
  mixins: [HasMode, CheckPermission],
  name: 'TargetDefinition',
  props: {
    loading: Boolean,
    mode: String
  },
  components: {
    Accordion,
    AccordionItem,
    Alert,
    Card,
    Draggable,
    Group,
    MenuButton,
    Modal,
    NewGroupModal: () => import('../campaign-manager/sales/target-definition/NewGroupModal'),
    RuleGroups,
    SaveBookmarkModalFromTargetDefinition: SaveBookmarkModal,
    SLDSInput,
    Spinner
  },
  data () {
    return {
      brands: [],
      bookmarks: [],
      bookmarkToDeleteId: '',
      config: {},
      drag: false,
      filteredBookmarks:[],
      localSearchKey: '',
      localValue: {},
      newGroup: {
        operator: '',
        segmentName: ''
      },
      operators: [
        {
          label: this.$t('operators.AND'),
          value: 'AND'
        },
        {
          label: this.$t('operators.OR'),
          value: 'OR'
        },
        {
          label: this.$t('operators.NOT'),
          value: 'NOT'
        }
      ],
      pageNumber: 0,
      showPreviewModal: false,
      showSaveBookmarkModal: false,
      showAddGroupModal: false,
      standardRules: {}
    }
  },
  methods: {
    goToFirstPage () {
      this.currentAnimation = 'slide-fade-left-delay'
      this.pageNumber = 0
    },
    goToLastPage () {
      this.currentAnimation = 'slide-fade-left-delay'
      this.pageNumber = this.pageNumbers
    },
    goToNextPage () {
      this.currentAnimation = 'slide-fade-left-delay'
      this.pageNumber++
    },
    goToPreviousPage () {
      this.currentAnimation = 'slide-fade-right-delay'
      this.pageNumber--
    },
    applyFilters: _.debounce((list, searchKey, filtersObject, fieldsToFilter, sortingOptions, callback) => applyFiltersToList(list, searchKey, filtersObject, fieldsToFilter, sortingOptions, callback), 300),
    filterBookmarks(){
      this.applyFilters(this.bookmarks, this.localSearchKey, {}, ['segmentName'], null, (result) => { this.filteredBookmarks = result })
    },
    getBookmarkActions(bookmark){
      return  bookmark.createdById === this.$store.getters['session/userId'] || 
              this.checkPermission('DELETE_FAST_COUNTER') ? 
              ['import','delete'] : 
              ['import']
    },
    async doDeleteBookmark () {
      try {
        this.deleteBookmark(this.bookmarkToDeleteId)
        await this.getBookmarks()
        this.bookmarkToDeleteId = ''
        this.showToast('success', this.$t('message.delete'), 'text')
      } catch (error) {
        console.error(error)
        this.showToast('error', this.$t('general.error'), 'text', error)
      }
      EventBus.$emit('bookmark-deleted')
    },
    dragStart () {
      EventBus.$emit('start-drag', { source: 'bookmark' })
    },
    async dragEnd () {
      EventBus.$emit('end-drag', { source: 'bookmark' })
      await this.getBookmarks()
    },
    ...mapActions({
      deleteBookmark: 'salesQueryBuilder/deleteBookmark',
      fetchBookmarks: 'salesQueryBuilder/getBookmarks',
      getSettings: 'fastCounter/getConfig'
    }),
    async handleSelect (event, bookmark) {
      if (event.value === 'import') {
        this.localValue = _.clone(bookmark)
      } else if (event.value === 'delete') {
        this.bookmarkToDeleteId = bookmark.id
      }
    },
    createFromBookmark (bookmark) {
      this.localValue = {
        conditions: [],
        ...bookmark
      }

      this.closeModal()
    },
    createGroup (event) {
      const { segmentName, operator } = event

      this.localValue = {
        // id: 'item_0',
        segmentName: segmentName,
        type: 'group',
        operator: operator,
        conditions: []
      }

      this.closeModal()
    },
    addRule () {
      this.localValue = {
        type: 'rule',
        table: '',
        field: '',
        operator: '',
        values: ''
      }
    },
    addRuleGroup () {
      const valid = validate(this, 'newGroup_', true)
      if (valid) {
        this.localValue = {
          segmentName: this.newGroup.segmentName,
          type: 'group',
          operator: this.newGroup.operator,
          conditions: []
        }

        this.closeModal()
      }
    },
    async callLoadFromStore () {
      const preview = this.$store.getters['fastCounter/state'].preview
      this.brands = preview.brands

      let config = await readFirstLine('salesQueryBuilderConfig')
      if ('value' in config) {
        config = config.value.config
      } else {
        config = await this.getSettings()
        const db = await getDb()
        insert(db, 'salesQueryBuilderConfig', { key: 'CONFIG', value: { config }})
      }

      this.config = Object.entries(config).reduce((acc, curr) => {
        const [ key, value ] = curr
        if (value.targetSource && value.targetSource.includes(TARGET_SOURCE)) {
          acc[key] = value
        }
        return acc
      }, {})

      await this.getBookmarks()
      const filters = _.cloneDeep(preview.config.targetDefinition)
      this.localValue = filters.selected
      this.standardRules = filters.standard
      EventBus.$on('bookmark-added', async () => {
        await this.getBookmarks()
      })
      EventBus.$on('bookmark-deleted', async () => {
        await this.getBookmarks()
      })
    },
    closeModal () {
      this.showAddGroupModal = false
    },
    openBookmarkModal () {
      if (this.validate()) {
        this.showSaveBookmarkModal = true
      }
    },
    openPreviewModal () {
      if (this.validate()) {
        this.showPreviewModal = true
      }
    },
    deleteRule () {
      this.localValue = {}
    },
    async getBookmarks () {
      try {
        const bookmarks = await this.fetchBookmarks({ scope: 'fastCounter' })
        this.bookmarks = bookmarks.map(bookmark => {
          return {
            ...bookmark.section,
            id: bookmark.id,
            createdById: bookmark.createdById,
            createdByName: bookmark.createdByName,
            createdDate: bookmark.createdDate
          }
        })
        this.filteredBookmarks = this.bookmarks
        this.filterBookmarks()
      } catch (error) {
        console.error(error)
        this.showToast('error', this.$t('general.error'), 'text', error)
      }
    },
    getSectionToSave () {
      return {
        config: {
          selected: this.localValue,
          standard: this.standardRules
        }
      }
    },
    showModal () {
      this.showAddGroupModal = true
    },
    validate () {
      return this.$refs.groups.validate()
    }
  },
  async activated () {
    this.$emit('startLoading')
    await this.callLoadFromStore()
    this.$emit('endLoading')
  },
  computed: {
    rowsToShow () {
      if (!this.showPaginationSection) {
        return this.filteredBookmarks
      } else {
        return this.filteredBookmarks.slice(this.startIndex, this.endIndex + 1)
      }
    },
    endIndex () {
      return ((this.pageNumber + 1) * PAGE_SIZE - 1)
    },
    isNextButtonDisabled () {
      return this.filteredBookmarks.length <= (this.pageNumber + 1) * PAGE_SIZE
    },
    pageNumbers () {
      return Math.floor(this.filteredBookmarks.length / PAGE_SIZE)
    },
    showPaginationSection () {
      return (this.mode === 'edit' || this.mode === 'create')  && this.filteredBookmarks.length > PAGE_SIZE
    },
    startIndex () {
      return this.pageNumber * PAGE_SIZE
    },
    canAddRule () {
      return _.isEmpty(this.localValue)
    }
  }
}
</script>
<style lang="scss" scoped>
  .pagination-section {
    bottom: 10px;
    display: flex;
    width: 100%;
    align-items: center;

    & > div {
      margin: auto;
    }
  }

  .wrap {
    width: 5rem;
    word-break: break-word;
    white-space: pre-line;
  }

  .and {
    &.cls-1 {
      fill: #fff;
      stroke: #dddbda;
    }

    &.cls-1,
    &.cls-2 {
      stroke-miterlimit: 10;
      stroke-width: 1.5px;
    }

    &.cls-2 {
      fill: #4589ee;
      stroke: #4589ee;
    }

    &.cls-3 {
      fill: #a9ddf3;
    }

    &.cls-4 {
      font-size: 19px;
      fill: #c1c1c1;
      font-family: 'Salesforce Sans';
    }

  }

  .not {
    &.cls-1 {
      fill: #4589ee;
    }

    &.cls-1,
    &.cls-2 {
      stroke: #dddbda;
      stroke-miterlimit: 10;
      stroke-width: 1.5px;
    }

    &.cls-2 {
      fill: #fff;
    }

    &.cls-3 {
      font-size: 19px;
      fill: #c1c1c1;
      font-family: 'Salesforce Sans';
    }
  }

  .or {
    &.cls-1 {
      fill: #fff;
    }

    &.cls-1,
    &.cls-4 {
      stroke: #dddbda;
    }

    &.cls-1,
    &.cls-2,
    &.cls-4 {
      stroke-miterlimit: 10;
      stroke-width: 1.5px;
    }

    &.cls-2,
    &.cls-3,
    &.cls-4 {
      fill: #4589ee;
    }

    &.cls-2 {
      stroke: #4589ee;
    }
    &.cls-5 {
      font-size: 19px;
      fill: #c1c1c1;
      font-family: 'Salesforce Sans';
    }
  }

  .slds-text-body_small {
    color: grey !important;
  }
</style>
